import {CAMPAIGN} from "./campaign";
import {meritThreshold} from "../Components/lottery/hooks/pickMeritWinner";

/**
 * Date arithmetic over the campaign phases.
 *
 * The dates themselves live in config/campaign.js — this file is only the logic
 * that reads them, so there is one place to edit and one place to reason about.
 */

/**
 * Parses 'YYYY-MM-DD' into a local Date.
 *
 * Built by parts rather than handed to `new Date(string)`: the previous
 * '2025/10/22' slash form is not a format the spec defines, so engines were free
 * to disagree, and the ISO form '2025-10-22' would have been read as UTC —
 * eight hours off in Mongolia.
 */
const atStartOfDay = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0, 0);
};

/** Same, but the last instant of that day, so `closesAt` includes its own day. */
const atEndOfDay = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day, 23, 59, 59, 999);
};

export const PHASES = CAMPAIGN.phases
    .map(phase => ({
        id: phase.id,
        name: phase.name,
        winners: phase.winners,
        earning: phase.earning,
        // Normalized here so the draw always receives an array, and the same
        // array instance on every render.
        meritWinners: phase.meritWinners ?? [],
        // Derived here so no caller has to add the two up, and no total can
        // disagree with the split it came from.
        maxWinners: phase.winners.male + phase.winners.female,
        opensAt: atStartOfDay(phase.opensAt),
        revealsAt: atStartOfDay(phase.revealsAt),
        closesAt: atEndOfDay(phase.closesAt),
    }))
    // Sorted so "next" means next chronologically, not next in declaration order.
    .sort((a, b) => a.opensAt - b.opensAt);

// A mistyped date or a missing quota is otherwise invisible until the campaign
// silently misbehaves in front of an audience.
if (process.env.NODE_ENV !== 'production') {
    PHASES.forEach(phase => {
        if (!(phase.opensAt <= phase.revealsAt && phase.revealsAt <= phase.closesAt)) {
            console.error(
                `[happypay] Phase ${phase.id} has bounds out of order — ` +
                `expected opensAt <= revealsAt <= closesAt.`, phase
            );
        }
        if (!phase.name || !(phase.maxWinners > 0)) {
            console.error(
                `[happypay] Phase ${phase.id} is missing a name or a winners split.`, phase
            );
        }
        // Not `maxWinners`: the opening spins are drawn by chance before any merit
        // winner is due, so those prizes are never available to them.
        const meritSeats = phase.maxWinners - meritThreshold(phase.maxWinners);
        if (phase.meritWinners.length > meritSeats) {
            console.error(
                `[happypay] Phase ${phase.id} names ${phase.meritWinners.length} employee merit ` +
                `winners but has room for at most ${meritSeats} of them ` +
                `(${phase.maxWinners} prizes, the first ${phase.maxWinners - meritSeats} drawn by ` +
                `chance) — the surplus can never be drawn.`, phase
            );
        }
        const e = phase.earning;
        if (!e?.transactions?.per || !e?.referrals?.per || !e?.cardLinked) {
            console.error(
                `[happypay] Phase ${phase.id} has incomplete earning rules — ` +
                `they must mirror LOTTERY_ITERATIONS[${phase.id}]['rules'] on the backend.`, phase
            );
        }
    });
}

/**
 * The phase the system is currently in: the most recent one to have opened.
 *
 * Deliberately ignores closesAt. A phase's viewing window runs a day or two past
 * its draw, while the next phase's earning period starts the same day as that
 * draw — so the windows overlap and "first window containing now" would answer
 * with the older phase. "Latest opened" has no such ambiguity and gives the same
 * answer as the backend's getCurrentIteration() in every case: inside a window,
 * after the last one, and before the first.
 *
 * @returns {object|null}
 */
export const getActivePhase = (now = new Date()) => {
    if (PHASES.length === 0) return null;

    const opened = PHASES.filter(phase => now >= phase.opensAt);
    return opened.length > 0 ? opened[opened.length - 1] : PHASES[0];
};

/**
 * Zeroed rules, used only when there is no configured phase at all — the
 * dev-mode check above will already have reported that. `per: 1` rather than 0
 * keeps the progress arithmetic from dividing by zero; the cards simply read as
 * "nothing earned yet" instead of throwing.
 */
const NO_EARNING = {
    transactions: {per: 1, tickets: 0},
    referrals: {per: 1, tickets: 0},
    cardLinked: {tickets: 0},
};

/**
 * Earning rules for the phase the customer's `lotteryCount` was computed under.
 *
 * Deliberately keyed off the same getActivePhase() the draw screen uses, because
 * the backend computes lotteryCount with getCurrentIteration()'s rules — read a
 * different phase's rules here and the cards would explain the total wrongly.
 */
export const getActiveEarning = (now = new Date()) => getActivePhase(now)?.earning ?? NO_EARNING;

/**
 * The phase whose tickets can be shown right now: the draw has happened and the
 * window has not closed.
 * @returns {object|null}
 */
export const getViewablePhase = (now = new Date()) =>
    PHASES.find(phase => now >= phase.revealsAt && now <= phase.closesAt) || null;

/**
 * The next draw still in the future, or null when the campaign is over.
 * @returns {Date|null}
 */
export const getNextReveal = (now = new Date()) =>
    PHASES.find(phase => phase.revealsAt > now)?.revealsAt || null;

/**
 * What the countdown counts toward.
 *
 * Always a future date or null. The old version returned the current phase's
 * reveal even once it had passed, so for the rest of that window the timer was
 * handed a date in the past and just showed its placeholder.
 * @returns {Date|null}
 */
export const getTimerDate = (now = new Date()) => getNextReveal(now);

/**
 * YYYY/MM/DD. Explicit rather than `toLocaleDateString('ja-JA')` — that tag is
 * not a real locale and only produced this format by falling back.
 */
export const formatCampaignDate = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
};
